import React, { useState, useEffect, useCallback } from 'react';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 18px' };
const LBL = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
const routeAbsente = (r) => r.status === 404 || r.status === 405 || r.status === 501;

// ── Listes de diffusion (audiences sourcia) ──────────────────────────────────
// La modale « Diffuser aux sourceurs » propose ces listes : un besoin diffusé
// avec une liste ne part qu'à ses membres. « Tous les sourceurs » est la liste
// intégrée du serveur — elle ne se modifie pas ici.
export function ListesDiffusion({ sourceurs = [], showNotification }) {
  const [listes, setListes] = useState(null);   // null = chargement, 'absent' = route pas servie
  const [edition, setEdition] = useState(null); // { id?, name, ids:Set } — création si id absent
  const [busy, setBusy] = useState(false);
  const actifs = sourceurs.filter(s => s.status === 'active');

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/diffusion/audiences`, { headers: authH() });
      if (routeAbsente(r)) { setListes('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const arr = j.audiences || j.data || (Array.isArray(j) ? j : []);
      setListes(Array.isArray(arr) ? arr : []);
    } catch { setListes('absent'); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const membresDe = (l) => Array.isArray(l.sourceur_ids) ? l.sourceur_ids : (Array.isArray(l.members) ? l.members.map(m => m.id ?? m) : null);
  const integree = (l) => l.id === 'all' || l.builtin === true;

  const enregistrer = async () => {
    const nom = (edition?.name || '').trim();
    if (!nom) { showNotification?.('Donnez un nom à la liste.', 'error'); return; }
    setBusy(true);
    try {
      const creation = edition.id == null;
      const r = await fetch(`${BASE}/diffusion/audiences${creation ? '' : `/${encodeURIComponent(edition.id)}`}`, {
        method: creation ? 'POST' : 'PUT', headers: jsonH(),
        body: JSON.stringify({ name: nom, sourceur_ids: [...edition.ids] }),
      });
      if (routeAbsente(r)) { showNotification?.('Les listes de diffusion arrivent côté serveur — rien n\'a été enregistré.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!(r.ok || r.status === 201) || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || (creation ? `Liste « ${nom} » créée — elle apparaît dans la modale de diffusion.` : 'Liste mise à jour.'), 'success');
      setEdition(null);
      charger();
    } catch (e) { showNotification?.('Enregistrement impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const supprimer = async (l) => {
    if (!await confirmErpia({ message: `Supprimer la liste « ${l.name} » ?\n\nElle ne sera plus proposée à la diffusion. Les besoins déjà diffusés avec cette liste ne sont PAS coupés — ils se gèrent dans « Besoins diffusés » ci-dessous.`, confirmLabel: 'Supprimer la liste' })) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/diffusion/audiences/${encodeURIComponent(l.id)}`, { method: 'DELETE', headers: authH() });
      if (routeAbsente(r)) { showNotification?.('Les listes de diffusion arrivent côté serveur — rien n\'a été supprimé.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || `Liste « ${l.name} » supprimée.`, 'success');
      charger();
    } catch (e) { showNotification?.('Suppression impossible : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const nomSourceur = (s) => `${s.firstName} ${s.lastName}`.trim() || s.email || `Sourceur ${s.id}`;

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
        <span style={LBL}>Listes de diffusion</span>
        <span style={{ flex: 1 }} />
        {edition == null && <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setEdition({ name: '', ids: new Set() })}><BI icon="plus-lg" /> Nouvelle liste</button>}
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '12px', lineHeight: 1.5, color: 'var(--text-2)' }}>
        Un besoin diffusé avec une liste ne part qu'à ses membres — c'est le choix « Liste de diffusion » de la fenêtre « Diffuser aux sourceurs ». Sans liste choisie, la diffusion part à tous les sourceurs actifs.
      </p>

      {listes === null ? <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</p>
        : listes === 'absent' ? <p style={{ margin: 0, padding: '10px 12px', borderRadius: '8px', background: 'var(--code-bg)', fontSize: '12.5px', color: 'var(--text-2)' }}>Les listes de diffusion arrivent côté serveur — en attendant, chaque diffusion part à tous les sourceurs actifs.</p>
        : (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 0', borderTop: '1px solid var(--hair)' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>Tous les sourceurs</span>
            <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-3)' }}>{actifs.length} actif{actifs.length > 1 ? 's' : ''}</span>
            <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}>intégrée</span>
          </div>
          {listes.filter(l => !integree(l)).map(l => {
            const m = membresDe(l);
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 0', borderTop: '1px solid var(--hair)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</span>
                <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-3)', flex: 'none' }}>{m != null ? `${m.length} membre${m.length > 1 ? 's' : ''}` : (l.count != null ? `${l.count}` : '—')}</span>
                <button className="erp-btn erp-btn--secondary erp-btn--sm" disabled={busy} onClick={() => setEdition({ id: l.id, name: l.name || '', ids: new Set(m || []) })}>Modifier</button>
                <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={busy} onClick={() => supprimer(l)} title="Supprimer la liste" aria-label={`Supprimer la liste ${l.name}`}><BI icon="trash3" /></button>
              </div>
            );
          })}
          {listes.filter(l => !integree(l)).length === 0 && edition == null && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-faint)' }}>Aucune liste — créez-en une pour cibler la diffusion (ex. « Spécialistes IT », « Réseau Suisse romande »).</p>
          )}
        </>)}

      {edition != null && (
        <div style={{ marginTop: '12px', padding: '13px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--panel)' }}>
          <div style={{ ...LBL, fontSize: '9.5px', marginBottom: '7px' }}>{edition.id == null ? 'Nouvelle liste' : 'Modifier la liste'}</div>
          <input className="erp-field" value={edition.name} autoFocus placeholder="Nom de la liste — ex. Spécialistes IT"
            onChange={e => setEdition(p => ({ ...p, name: e.target.value }))} style={{ width: '100%' }} aria-label="Nom de la liste" />
          <div style={{ ...LBL, fontSize: '9.5px', margin: '12px 0 7px' }}>Membres — sourceurs actifs</div>
          {actifs.length === 0 ? <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-faint)' }}>Aucun sourceur actif pour l'instant — validez des inscriptions ci-dessus.</p> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '4px 12px', maxHeight: '220px', overflowY: 'auto' }}>
              {actifs.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 2px', fontSize: '12.5px', color: 'var(--text)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={edition.ids.has(s.id)}
                    onChange={() => setEdition(p => { const ids = new Set(p.ids); ids.has(s.id) ? ids.delete(s.id) : ids.add(s.id); return { ...p, ids }; })} />
                  <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nomSourceur(s)}{s.email && <span style={{ color: 'var(--text-3)' }}> · {s.email}</span>}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <span style={{ flex: 1, fontSize: '11.5px', color: 'var(--text-3)' }}>{edition.ids.size} membre{edition.ids.size > 1 ? 's' : ''} sélectionné{edition.ids.size > 1 ? 's' : ''}</span>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setEdition(null)}>Annuler</button>
            <button className="erp-btn erp-btn--primary erp-btn--sm" disabled={busy || !edition.name.trim()} onClick={enregistrer}>{busy ? 'Enregistrement…' : edition.id == null ? 'Créer la liste' : 'Enregistrer'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Besoins diffusés sur sourcia — vue globale + coupure ─────────────────────
export function DiffusionsEnCours({ showNotification }) {
  const [rows, setRows] = useState(null); // null = chargement, 'absent' = route pas servie
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/diffusion/besoins`, { headers: authH() });
      if (routeAbsente(r)) { setRows('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const arr = j.diffusions || j.besoins || j.data || (Array.isArray(j) ? j : []);
      setRows((Array.isArray(arr) ? arr : []).map(d => ({
        besoinId: d.besoin_id ?? d.besoinId ?? d.id,
        titre: d.titre || d.title || d.poste || `Besoin ${d.besoin_id ?? d.id}`,
        ref: d.public_ref || d.ref || null,
        audience: d.audience_name || d.audience?.name || null,
        publieLe: d.published_at || d.publie_le || null,
        propositions: d.proposals_count ?? d.propositions ?? null,
      })).filter(d => d.besoinId != null));
      setSel(new Set());
    } catch { setRows('absent'); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // La coupure passe par la route UNITAIRE déjà en place (même contrôle que le
  // bouton du besoin) — un appel par besoin, bilan honnête besoin par besoin.
  const couper = async (ids) => {
    if (!ids.length) return;
    const noms = rows !== 'absent' ? ids.slice(0, 4).map(id => `« ${(rows.find(r => r.besoinId === id) || {}).titre} »`).join(', ') + (ids.length > 4 ? '…' : '') : '';
    if (!await confirmErpia({ message: `Couper la diffusion de ${ids.length} besoin${ids.length > 1 ? 's' : ''} ?\n${noms}\n\nLes sourceurs ne verront plus ce${ids.length > 1 ? 's' : ''} besoin${ids.length > 1 ? 's' : ''} sur sourcia. Les propositions déjà reçues restent consultables, et le besoin peut être rediffusé plus tard.`, confirmLabel: 'Couper la diffusion' })) return;
    setBusy(true);
    const echecs = [];
    let faits = 0;
    for (const id of ids) {
      try {
        const r = await fetch(`${BASE}/besoins/${id}/diffusion`, { method: 'DELETE', headers: authH() });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
        faits += 1;
      } catch (e) { const t = (rows.find(x => x.besoinId === id) || {}).titre || id; echecs.push(`${t} (${e.message})`); }
    }
    setBusy(false);
    showNotification?.(echecs.length === 0
      ? `Diffusion coupée pour ${faits} besoin${faits > 1 ? 's' : ''}.`
      : `${faits} coupée${faits > 1 ? 's' : ''}, ${echecs.length} en échec : ${echecs.join(' · ')}`,
      echecs.length ? 'error' : 'success');
    charger();
  };

  const bascule = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
        <span style={LBL}>Besoins diffusés sur sourcia</span>
        {Array.isArray(rows) && <span className="ats-mono" style={{ fontSize: '10.5px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '1px 7px', borderRadius: '6px' }}>{rows.length}</span>}
        <span style={{ flex: 1 }} />
        {sel.size > 0 && <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={busy} onClick={() => couper([...sel])}><BI icon="broadcast" /> Couper la diffusion ({sel.size})</button>}
      </div>
      <p style={{ margin: '0 0 12px', fontSize: '12px', lineHeight: 1.5, color: 'var(--text-2)' }}>
        Tout ce que les sourceurs voient en ce moment. Couper une diffusion retire le besoin de sourcia — les propositions déjà reçues restent, et le besoin peut être rediffusé depuis sa fiche.
      </p>

      {rows === null ? <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</p>
        : rows === 'absent' ? <p style={{ margin: 0, padding: '10px 12px', borderRadius: '8px', background: 'var(--code-bg)', fontSize: '12.5px', color: 'var(--text-2)' }}>La vue globale des diffusions arrive côté serveur — en attendant, la diffusion de chaque besoin se gère depuis sa fiche (bouton « Gérer la diffusion »).</p>
        : rows.length === 0 ? <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun besoin diffusé en ce moment.</p>
        : rows.map((d, i) => (
          <div key={d.besoinId} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderTop: i === 0 ? '1px solid var(--hair)' : '1px solid var(--hair)' }}>
            <input type="checkbox" checked={sel.has(d.besoinId)} onChange={() => bascule(d.besoinId)} aria-label={`Sélectionner ${d.titre}`} style={{ flex: 'none' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.titre}{d.ref && <span className="ats-mono" style={{ marginLeft: '7px', fontSize: '10.5px', fontWeight: 400, color: 'var(--text-3)' }}>{d.ref}</span>}</div>
              <div style={{ marginTop: '2px', fontSize: '11px', color: 'var(--text-3)' }}>
                {[d.audience || 'Tous les sourceurs', d.publieLe ? `diffusé le ${new Date(d.publieLe).toLocaleDateString('fr-FR')}` : null, d.propositions != null ? `${d.propositions} proposition${d.propositions > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button className="erp-btn erp-btn--danger erp-btn--sm" disabled={busy} onClick={() => couper([d.besoinId])}>Couper</button>
          </div>
        ))}
    </div>
  );
}
